import { useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import CreativeDirectorCard from "./CreativeDirectorCard";
import GlassButton from "./GlassButton";

/**
 * <CreativeDirectorBrowseModal/> — the "Browse All Styles" modal.
 *
 * Reads from the SAME `directors` array the parent already fetched from
 * /api/creative-directors — so library_only records are ALREADY filtered out
 * server-side, and hidden_prompt is guaranteed absent (public projection).
 *
 * Focus semantics:
 *   • Closes on Escape.
 *   • Backdrop click closes.
 *   • Focus is trapped inside the dialog while open.
 *   • On close, focus is returned to whatever element opened it.
 *
 * Sort options: Featured → Fresh → alphabetical → most-recent by sort_order.
 */

function _norm(s) {
  return (s || "").trim().toLowerCase();
}

export default function CreativeDirectorBrowseModal({ open, onClose, directors, selected, onSelect }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const inputRef = useRef(null);
  const openerRef = useRef(null);
  const dialogRef = useRef(null);

  // Track the previously-focused element so we can restore on close.
  useEffect(() => {
    if (open) openerRef.current = document.activeElement;
    else if (openerRef.current && typeof openerRef.current.focus === "function") {
      try { openerRef.current.focus(); } catch (_) { /* no-op */ }
    }
  }, [open]);

  // Autofocus the search input + trap focus.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { try { inputRef.current?.focus(); } catch (_) {} }, 40);
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose?.(); return; }
      if (e.key !== "Tab") return;
      const root = dialogRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    // Prevent body scroll while the modal is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Build category list dynamically from actual data — normalized for dedupe
  // (case-insensitive) but original casing preserved for display. "All Styles"
  // is always first. Known categories float to the top when present.
  const categories = useMemo(() => {
    const KNOWN = ["Party", "Mixtape", "Corporate", "Church", "Casino", "Events", "Seasonal"];
    const seen = new Map();  // norm -> display
    for (const d of directors || []) {
      const raw = (d.category || "").trim();
      if (!raw) continue;
      const n = _norm(raw);
      if (!seen.has(n)) seen.set(n, raw);
    }
    const known = KNOWN.filter((k) => seen.has(_norm(k)));
    const other = [...seen.values()]
      .filter((v) => !known.map(_norm).includes(_norm(v)))
      .sort((a, b) => a.localeCompare(b));
    return [{ id: "all", label: "All Styles" }, ...known.map((k) => ({ id: _norm(k), label: k })),
                                                  ...other.map((k) => ({ id: _norm(k), label: k }))];
  }, [directors]);

  // Filter + sort — case-insensitive across name, category, tags, keywords.
  const filtered = useMemo(() => {
    const nq = _norm(q);
    const filterMatches = (d) => {
      if (cat !== "all" && _norm(d.category) !== cat) return false;
      if (!nq) return true;
      if (_norm(d.name).includes(nq)) return true;
      if (_norm(d.short_description).includes(nq)) return true;
      if (_norm(d.category).includes(nq)) return true;
      if (Array.isArray(d.tags) && d.tags.some((t) => _norm(t).includes(nq))) return true;
      if (Array.isArray(d.keywords) && d.keywords.some((k) => _norm(k).includes(nq))) return true;
      return false;
    };
    const arr = (directors || []).filter(filterMatches);
    // Sort: featured > fresh > lower sort_order > name asc
    arr.sort((a, b) => {
      const fa = a.is_featured ? 1 : 0, fb = b.is_featured ? 1 : 0;
      if (fa !== fb) return fb - fa;
      const ra = a.is_fresh_drop ? 1 : 0, rb = b.is_fresh_drop ? 1 : 0;
      if (ra !== rb) return rb - ra;
      const sa = a.sort_order ?? 999, sb = b.sort_order ?? 999;
      if (sa !== sb) return sa - sb;
      return (a.name || "").localeCompare(b.name || "");
    });
    return arr;
  }, [directors, q, cat]);

  if (!open) return null;

  return (
    <div
      data-testid="cds-browse-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Browse all Creative Director Styles"
      className="fixed inset-0 z-[95] flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />

      <div
        ref={dialogRef}
        className="relative w-full max-w-6xl max-h-[88vh] rounded-3xl bg-[linear-gradient(180deg,#030303_0%,#000000_100%)] flex flex-col overflow-hidden"
        style={{
          boxShadow: [
            "inset 0 2px 0 rgba(255,255,255,0.16)",
            "inset 0 0 0 1px rgba(255,255,255,0.06)",
            "inset 0 -2px 0 rgba(0,0,0,0.55)",
            "0 30px 80px -30px rgba(0,0,0,0.95)",
          ].join(", "),
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-5 border-b border-white/5 shrink-0">
          <div className="flex-1 min-w-0">
            <p className="font-display text-lg font-bold tracking-tight">Browse All Creative Director Styles</p>
            <p className="text-[11px] text-zinc-500">
              {filtered.length} of {directors?.length || 0} styles
            </p>
          </div>
          <button
            data-testid="cds-browse-close"
            onClick={onClose}
            className="h-9 w-9 rounded-full bg-white/5 hover:bg-white/15 text-zinc-300 hover:text-white flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Search + filter row */}
        <div className="px-5 pt-4 pb-3 space-y-3 shrink-0">
          <div className="relative">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              ref={inputRef}
              data-testid="cds-browse-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, category, tag…"
              className="w-full h-10 pl-9 pr-3 rounded-full bg-black/60 border border-white/10 text-sm outline-none focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/30 transition-colors"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
            {categories.map((c) => (
              <GlassButton
                key={c.id}
                data-testid={`cds-browse-cat-${c.id}`}
                tone={cat === c.id ? "gold" : "chrome"}
                size="sm"
                onClick={() => setCat(c.id)}
              >
                {c.label}
              </GlassButton>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto px-5 pb-5">
          {filtered.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-xs text-zinc-500">
              No styles match your search.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 justify-items-center py-3">
              {filtered.map((d) => (
                <CreativeDirectorCard
                  key={d.id}
                  director={d}
                  selected={selected?.id === d.id}
                  onSelect={(next) => {
                    onSelect?.(next);
                    // Close after a positive selection; deselect keeps the modal open.
                    if (next) onClose?.();
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
